import {
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import {
    getUbiquitiMostCommonDevices,
    getUbiquitiTopOverview,
} from "../../scripts/api_ubiquiti";

/* -------------------------------------------------------------------------- */
/*                                   COMMON                                   */
/* -------------------------------------------------------------------------- */

const QUERY_OPTIONS = {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30000,
};

const TITLE_LENGTH = 7;

const useSafeLayoutEffect =
    typeof window === "undefined" ? useEffect : useLayoutEffect;

const hasValue = (value) =>
    value !== undefined && value !== null && value !== "";

const normalizeWidth = (value) => {
    if (!Number.isFinite(value)) {
        return 0;
    }

    return Math.max(0, Math.floor(value));
};

const getWrappedPayload = (response) => {
    const firstPayload = response?.data ?? response ?? {};

    return firstPayload?.data ?? firstPayload ?? {};
};

const getDisplayValue = (value, fallback = "-") =>
    value === undefined || value === null || value === ""
        ? fallback
        : value;

const getCompactTitle = (value) => {
    const title = String(getDisplayValue(value));

    return title.length > TITLE_LENGTH
        ? `${title.slice(0, TITLE_LENGTH)}...`
        : title;
};

const includesAny = (value, words) =>
    words.some((word) => value.includes(word));

/* -------------------------------------------------------------------------- */
/*                              CONTAINER WIDTH                               */
/* -------------------------------------------------------------------------- */
export const useUbiquitiContainerWidth = () => {
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(null);

    useSafeLayoutEffect(() => {
        const element = containerRef.current;

        if (!element || typeof window === "undefined") {
            return undefined;
        }

        let animationFrameId = null;
        let disposed = false;

        const updateWidth = () => {
            if (disposed) {
                return;
            }

            const nextWidth = normalizeWidth(
                element.getBoundingClientRect().width,
            );

            setContainerWidth((previousWidth) =>
                previousWidth === nextWidth ? previousWidth : nextWidth,
            );
        };

        const scheduleUpdate = () => {
            if (disposed) {
                return;
            }

            if (typeof window.requestAnimationFrame !== "function") {
                updateWidth();
                return;
            }

            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }

            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                updateWidth();
            });
        };

        // Mesure initiale synchrone avant le paint côté navigateur.
        updateWidth();

        if (typeof ResizeObserver !== "undefined") {
            const resizeObserver = new ResizeObserver(scheduleUpdate);

            resizeObserver.observe(element);

            return () => {
                disposed = true;
                resizeObserver.disconnect();

                if (
                    animationFrameId !== null &&
                    typeof window.cancelAnimationFrame === "function"
                ) {
                    window.cancelAnimationFrame(animationFrameId);
                }
            };
        }

        window.addEventListener("resize", scheduleUpdate, {
            passive: true,
        });

        return () => {
            disposed = true;
            window.removeEventListener("resize", scheduleUpdate);

            if (
                animationFrameId !== null &&
                typeof window.cancelAnimationFrame === "function"
            ) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, []);

    return {
        containerRef,
        containerWidth,
    };
};

// Compatibilité avec les imports existants.
export const useMostCommonContainerWidth =
    useUbiquitiContainerWidth;

export const useTopOverviewContainerWidth =
    useUbiquitiContainerWidth;

/* -------------------------------------------------------------------------- */
/*                                QUERY COMMON                                */
/* -------------------------------------------------------------------------- */

const useQueryStateReporter = ({
    isError,
    isFetched,
    isFetching,
    onQueryStateChange,
    siteId,
}) => {
    const callbackRef = useRef(onQueryStateChange);
    const previousStateRef = useRef(null);

    useEffect(() => {
        callbackRef.current = onQueryStateChange;
    }, [onQueryStateChange]);

    useEffect(() => {
        const previousState = previousStateRef.current;

        const hasChanged =
            !previousState ||
            previousState.siteId !== siteId ||
            previousState.isError !== isError ||
            previousState.isFetched !== isFetched ||
            previousState.isFetching !== isFetching;

        if (!hasChanged) {
            return;
        }

        const nextState = {
            siteId,
            isError,
            isFetched,
            isFetching,
        };

        previousStateRef.current = nextState;

        callbackRef.current?.(nextState);
    }, [
        siteId,
        isError,
        isFetched,
        isFetching,
    ]);
};

/**
 * Evite le double fetch au montage :
 *
 * ancien comportement :
 * - useQuery démarre
 * - refreshKey déjà != 0
 * - useEffect appelle refetch()
 *
 * => potentiellement 2 requêtes.
 */
const useRefreshKeyRefetch = ({
    apiUrl,
    enabled,
    refreshKey,
    refetch,
    siteId,
}) => {
    const previousRef = useRef({
        apiUrl,
        refreshKey,
        siteId,
    });

    useEffect(() => {
        const previous = previousRef.current;

        const identityChanged =
            previous.apiUrl !== apiUrl ||
            previous.siteId !== siteId;

        const refreshChanged =
            !Object.is(previous.refreshKey, refreshKey);

        previousRef.current = {
            apiUrl,
            refreshKey,
            siteId,
        };

        // Un changement d'API/site est déjà pris en charge
        // automatiquement par le queryKey.
        if (
            enabled &&
            refreshChanged &&
            !identityChanged
        ) {
            refetch();
        }
    }, [
        apiUrl,
        enabled,
        refreshKey,
        refetch,
        siteId,
    ]);
};

const useUbiquitiQuery = ({
    apiUrl,
    fetcher,
    onQueryStateChange,
    queryName,
    refreshKey,
    siteId,
    enabled,
}) => {
    const queryEnabled =
        Boolean(enabled) &&
        hasValue(apiUrl) &&
        hasValue(siteId);

    const query = useQuery({
        ...QUERY_OPTIONS,
        queryKey: [queryName, apiUrl, siteId],
        queryFn: () => fetcher(apiUrl, siteId),
        enabled: queryEnabled,
    });

    useRefreshKeyRefetch({
        apiUrl,
        enabled: queryEnabled,
        refreshKey,
        refetch: query.refetch,
        siteId,
    });

    useQueryStateReporter({
        siteId,
        isError: query.isError,
        isFetched: query.isFetched,
        isFetching: query.isFetching,
        onQueryStateChange,
    });

    /*
     * enabled=false ne doit PAS afficher un skeleton éternellement.
     */
    const isInitialLoading =
        queryEnabled &&
        query.data === undefined &&
        query.isLoading;

    return {
        query,
        queryEnabled,
        isInitialLoading,
    };
};

/* -------------------------------------------------------------------------- */
/*                          MOST COMMON DEVICES                               */
/* -------------------------------------------------------------------------- */

const MOST_COMMON_CONTAINER_PADDING = 24;
const MOST_COMMON_ITEM_WIDTH = 48;
const MOST_COMMON_ITEM_GAP = 10;
const MOST_COMMON_SKELETON_COUNT = 18;

export const getMostCommonDisplayValue = getDisplayValue;

export const getMostCommonCompactTitle = getCompactTitle;

export const getMostCommonRows = (overview) =>
    Array.isArray(overview?.rows)
        ? overview.rows
        : [];

export const getMostCommonIconKind = (row) => {
    const title = String(
        row?.typeDisplay ??
        row?.type ??
        "",
    ).toLowerCase();

    if (
        includesAny(title, [
            "android",
            "iphone",
            "phone",
            "pixel",
            "galaxy",
        ])
    ) {
        return "smartphone";
    }

    if (
        includesAny(title, [
            "ipad",
            "tablet",
        ])
    ) {
        return "tablet";
    }

    if (title.includes("linux")) {
        return "terminal";
    }

    if (
        includesAny(title, [
            "windows",
            "mac",
            "pc",
            "laptop",
            "macbook",
        ])
    ) {
        return "monitor";
    }

    return "device";
};

/**
 * Aucun while.
 *
 * isConstrained=true signifie :
 * "Même un seul item ne tient pas proprement."
 *
 * On garde alors 1 item pour éviter de transformer
 * artificiellement des données existantes en "No data".
 */
export const getMostCommonLayout = (
    availableWidth,
    itemCount,
) => {
    const safeItemCount = Math.max(
        0,
        Number.isFinite(itemCount)
            ? Math.floor(itemCount)
            : 0,
    );

    if (safeItemCount === 0) {
        return {
            visibleCount: 0,
            isConstrained: false,
        };
    }

    // Largeur pas encore mesurée.
    if (availableWidth === null || availableWidth === undefined) {
        return {
            visibleCount: safeItemCount,
            isConstrained: false,
        };
    }

    const width = normalizeWidth(availableWidth);

    const contentWidth = Math.max(
        0,
        width - MOST_COMMON_CONTAINER_PADDING,
    );

    if (contentWidth < MOST_COMMON_ITEM_WIDTH) {
        return {
            visibleCount: 1,
            isConstrained: true,
        };
    }

    const visibleCount = Math.floor(
        (contentWidth + MOST_COMMON_ITEM_GAP) /
        (MOST_COMMON_ITEM_WIDTH + MOST_COMMON_ITEM_GAP),
    );

    return {
        visibleCount: Math.max(
            1,
            Math.min(safeItemCount, visibleCount),
        ),
        isConstrained: false,
    };
};

export const getMostCommonSkeletonLayout = (
    containerWidth,
) =>
    getMostCommonLayout(
        containerWidth,
        MOST_COMMON_SKELETON_COUNT,
    );

export const getMostCommonSkeletonItems = (
    containerWidth,
) => {
    const { visibleCount } =
        getMostCommonSkeletonLayout(containerWidth);

    return Array.from(
        { length: visibleCount },
        (_, index) => `most-common-skeleton-${index}`,
    );
};

export const useMostCommonLayout = (
    rows,
    containerWidth,
) => {
    const layout = useMemo(
        () =>
            getMostCommonLayout(
                containerWidth,
                rows.length,
            ),
        [containerWidth, rows.length],
    );

    const visibleRows = useMemo(
        () =>
            rows.slice(
                0,
                layout.visibleCount,
            ),
        [rows, layout.visibleCount],
    );

    return {
        visibleRows,
        isConstrained: layout.isConstrained,
    };
};

// Ancienne API conservée.
export const useVisibleMostCommonRows = (
    rows,
    containerWidth,
) =>
    useMostCommonLayout(
        rows,
        containerWidth,
    ).visibleRows;

export const useUbiquitiMostCommonDevices = ({
    apiUrl,
    enabled = true,
    onQueryStateChange,
    refreshKey = 0,
    siteId,
}) => {
    const {
        query,
        isInitialLoading,
    } = useUbiquitiQuery({
        apiUrl,
        enabled,
        fetcher: getUbiquitiMostCommonDevices,
        onQueryStateChange,
        queryName: "ubiquiti-most-common-devices",
        refreshKey,
        siteId,
    });

    const overview = useMemo(
        () => getWrappedPayload(query.data),
        [query.data],
    );

    const rows = useMemo(
        () => getMostCommonRows(overview),
        [overview],
    );

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.__ubiquitiMostCommonDevicesDebug = {
            apiCall: `/ubiquiti/sites/${siteId}/most-common-devices`,
            enabled,
            queryStatus: {
                isLoading: query.isLoading,
                isFetching: query.isFetching,
                isError: query.isError,
                error: query.error,
            },
            rawResponse: query.data,
            mappedCount: rows.length,
        };
    }, [
        enabled,
        query.data,
        query.error,
        query.isError,
        query.isFetching,
        query.isLoading,
        rows.length,
        siteId,
    ]);

    return {
        isInitialLoading,
        rows,
    };
};

/* -------------------------------------------------------------------------- */
/*                               TOP OVERVIEW                                 */
/* -------------------------------------------------------------------------- */

export const TOP_OVERVIEW_SECTIONS = [
    {
        key: "topAps",
        title: "Top APs",
        type: "ap",
    },
    {
        key: "topClients",
        title: "Top Clients",
        type: "client",
    },
    {
        key: "topApps",
        title: "Top Apps",
        type: "app",
    },
];

const TOP_OVERVIEW_SECTION_GAP = 18;
const TOP_OVERVIEW_CONTAINER_PADDING = 24;
const TOP_OVERVIEW_ITEM_WIDTH = 44;
const TOP_OVERVIEW_ITEM_GAP = 8;

const TOP_OVERVIEW_SECTION_MIN_WIDTHS = {
    topAps: 48,
    topClients: 70,
    topApps: 60,
};

const TOP_OVERVIEW_MIN_COUNTS = {
    topAps: 1,
    topClients: 1,
    topApps: 1,
};

const TOP_OVERVIEW_SKELETON = {
    topAps: Array.from({ length: 4 }),
    topClients: Array.from({ length: 12 }),
    topApps: Array.from({ length: 10 }),
};

export const getTopOverviewDisplayValue =
    getDisplayValue;

export const getTopOverviewCompactTitle =
    getCompactTitle;

export const getTopOverviewSectionItems = (
    overview,
    sectionKey,
) =>
    Array.isArray(overview?.[sectionKey])
        ? overview[sectionKey]
        : [];

export const getTopOverviewItemImage = (item) => {
    const candidates = [
        item?.imageUrl,
        item?.iconUrl,
        item?.icon,
        item?.logo,
        item?.appIcon,
    ];

    return (
        candidates.find(
            (value) =>
                typeof value === "string" &&
                value.trim().length > 0,
        ) ?? null
    );
};

export const getTopOverviewIconKind = (
    item,
    type,
) => {
    if (getTopOverviewItemImage(item)) {
        return "image";
    }

    if (type === "ap") {
        return "wifi";
    }

    if (type === "app") {
        return "globe";
    }

    const title = String(
        item?.title ?? "",
    ).toLowerCase();

    if (
        includesAny(title, [
            "phone",
            "flip",
            "ultra",
            "iphone",
        ])
    ) {
        return "smartphone";
    }

    if (
        includesAny(title, [
            "mac",
            "pc",
            "mvt",
        ])
    ) {
        return "monitor";
    }

    return "cpu";
};

export const shouldShowTopOverviewExperience = (
    type,
) => type !== "app";

export const getTopOverviewSkeletonItems = (
    section,
) =>
    Array.from(
        {
            length:
                section.type === "ap"
                    ? 4
                    : 10,
        },
        (_, index) =>
            `skeleton-${section.key}-${index}`,
    );

/**
 * Une section existe toujours dans le DOM,
 * même lorsqu'elle n'a aucune donnée.
 *
 * On conserve donc sa largeur minimum même count=0.
 */
const getTopOverviewSectionWidth = (
    sectionKey,
    count,
) => {
    const minimumWidth =
        TOP_OVERVIEW_SECTION_MIN_WIDTHS[
        sectionKey
        ] ?? 0;

    if (count <= 0) {
        return minimumWidth;
    }

    const itemsWidth =
        count * TOP_OVERVIEW_ITEM_WIDTH +
        Math.max(0, count - 1) *
        TOP_OVERVIEW_ITEM_GAP;

    return Math.max(
        minimumWidth,
        itemsWidth,
    );
};

const getTopOverviewLayoutWidth = (
    counts,
) => {
    const sectionsWidth =
        TOP_OVERVIEW_SECTIONS.reduce(
            (total, section) =>
                total +
                getTopOverviewSectionWidth(
                    section.key,
                    counts[section.key] ?? 0,
                ),
            0,
        );

    return (
        sectionsWidth +
        Math.max(
            0,
            TOP_OVERVIEW_SECTIONS.length - 1,
        ) *
        TOP_OVERVIEW_SECTION_GAP
    );
};

const getTopOverviewCounts = (
    overview,
) =>
    TOP_OVERVIEW_SECTIONS.reduce(
        (counts, section) => {
            counts[section.key] =
                getTopOverviewSectionItems(
                    overview,
                    section.key,
                ).length;

            return counts;
        },
        {},
    );

const getMinimumTopOverviewCounts = (
    maximumCounts,
) =>
    TOP_OVERVIEW_SECTIONS.reduce(
        (counts, section) => {
            const maximumCount =
                maximumCounts[section.key] ?? 0;

            counts[section.key] =
                maximumCount > 0
                    ? Math.min(
                        maximumCount,
                        TOP_OVERVIEW_MIN_COUNTS[
                        section.key
                        ] ?? 0,
                    )
                    : 0;

            return counts;
        },
        {},
    );

/**
 * Calcul O(nombre de sections).
 *
 * Aucun while.
 * Aucun risque de boucle infinie.
 *
 * Ordre de priorité conservé :
 * APs -> Clients -> Apps.
 */
export const getTopOverviewLayout = (
    availableWidth,
    overview,
) => {
    const maximumCounts =
        getTopOverviewCounts(overview);

    if (
        availableWidth === null ||
        availableWidth === undefined
    ) {
        return {
            counts: maximumCounts,
            isConstrained: false,
        };
    }

    const minimumCounts =
        getMinimumTopOverviewCounts(
            maximumCounts,
        );

    const width =
        normalizeWidth(availableWidth);

    const availableContentWidth = Math.max(
        0,
        width -
        TOP_OVERVIEW_CONTAINER_PADDING,
    );

    const minimumLayoutWidth =
        getTopOverviewLayoutWidth(
            minimumCounts,
        );

    /*
     * Cas impossible :
     *
     * Le navigateur n'a simplement pas assez
     * de pixels pour respecter toutes les minima.
     *
     * Pas de boucle, pas de tentative de réduction
     * infinie : on retourne immédiatement.
     */
    if (
        availableContentWidth <
        minimumLayoutWidth
    ) {
        return {
            counts: minimumCounts,
            isConstrained: true,
        };
    }

    const counts = {
        ...minimumCounts,
    };

    let remainingWidth =
        availableContentWidth -
        minimumLayoutWidth;

    /*
     * Maximum 3 itérations car il y a 3 sections.
     * Ce nombre ne dépend PAS de la quantité
     * de données ni de la largeur.
     */
    for (const section of TOP_OVERVIEW_SECTIONS) {
        const sectionKey = section.key;

        const currentCount =
            counts[sectionKey] ?? 0;

        const maximumCount =
            maximumCounts[sectionKey] ?? 0;

        if (
            maximumCount <= currentCount
        ) {
            continue;
        }

        const currentSectionWidth =
            getTopOverviewSectionWidth(
                sectionKey,
                currentCount,
            );

        const maximumSectionWidth =
            currentSectionWidth +
            remainingWidth;

        const maximumByWidth =
            Math.floor(
                (
                    maximumSectionWidth +
                    TOP_OVERVIEW_ITEM_GAP
                ) /
                (
                    TOP_OVERVIEW_ITEM_WIDTH +
                    TOP_OVERVIEW_ITEM_GAP
                ),
            );

        const nextCount = Math.max(
            currentCount,
            Math.min(
                maximumCount,
                maximumByWidth,
            ),
        );

        const nextSectionWidth =
            getTopOverviewSectionWidth(
                sectionKey,
                nextCount,
            );

        remainingWidth = Math.max(
            0,
            remainingWidth -
            (
                nextSectionWidth -
                currentSectionWidth
            ),
        );

        counts[sectionKey] =
            nextCount;
    }

    return {
        counts,
        isConstrained: false,
    };
};

export const getVisibleTopOverviewSkeletonItems = (
    section,
    containerWidth,
) => {
    const { counts } =
        getTopOverviewLayout(
            containerWidth,
            TOP_OVERVIEW_SKELETON,
        );

    return getTopOverviewSkeletonItems(
        section,
    ).slice(
        0,
        counts[section.key] ?? 0,
    );
};

export const useTopOverviewLayout = (
    overview,
    containerWidth,
) => {
    const layout = useMemo(
        () =>
            getTopOverviewLayout(
                containerWidth,
                overview,
            ),
        [containerWidth, overview],
    );

    const visibleOverview = useMemo(
        () =>
            TOP_OVERVIEW_SECTIONS.reduce(
                (result, section) => {
                    result[section.key] =
                        getTopOverviewSectionItems(
                            overview,
                            section.key,
                        ).slice(
                            0,
                            layout.counts[
                            section.key
                            ] ?? 0,
                        );

                    return result;
                },
                {},
            ),
        [layout.counts, overview],
    );

    return {
        visibleOverview,
        isConstrained:
            layout.isConstrained,
    };
};

// Compatibilité avec l'ancienne API.
export const useVisibleTopOverview = (
    overview,
    containerWidth,
) =>
    useTopOverviewLayout(
        overview,
        containerWidth,
    ).visibleOverview;

export const useUbiquitiTopOverview = ({
    apiUrl,
    enabled = true,
    onQueryStateChange,
    refreshKey = 0,
    siteId,
}) => {
    const {
        query,
        isInitialLoading,
    } = useUbiquitiQuery({
        apiUrl,
        enabled,
        fetcher: getUbiquitiTopOverview,
        onQueryStateChange,
        queryName: "ubiquiti-top-overview",
        refreshKey,
        siteId,
    });

    const overview = useMemo(
        () => getWrappedPayload(query.data),
        [query.data],
    );

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        window.__ubiquitiTopOverviewDebug = {
            apiCall: `/ubiquiti/sites/${siteId}/top-overview`,
            enabled,
            queryStatus: {
                isLoading: query.isLoading,
                isFetching: query.isFetching,
                isError: query.isError,
                error: query.error,
            },
            rawResponse: query.data,
            mappedCounts: {
                topAps:
                    getTopOverviewSectionItems(
                        overview,
                        "topAps",
                    ).length,
                topClients:
                    getTopOverviewSectionItems(
                        overview,
                        "topClients",
                    ).length,
                topApps:
                    getTopOverviewSectionItems(
                        overview,
                        "topApps",
                    ).length,
            },
        };
    }, [
        enabled,
        overview,
        query.data,
        query.error,
        query.isError,
        query.isFetching,
        query.isLoading,
        siteId,
    ]);

    return {
        isInitialLoading,
        overview,
    };
};